import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCw, Clock, Copy, Play, Pause } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { JumpToLatest } from "@/components/JumpToLatest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealtimeConsole = () => {
  const [incompleteCalls, setIncompleteCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcriptions, setTranscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const jumpToLatest = () => {
    const el = scrollAreaRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    fetchIncompleteCalls();
  }, []);

  useEffect(() => {
    if (autoRefresh && selectedCall) {
      // Auto-refresh every 3 seconds for real-time updates
      intervalRef.current = setInterval(() => {
        fetchTranscriptions(selectedCall, true);
      }, 3000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [autoRefresh, selectedCall]);

  const lastTranscriptionText = transcriptions.length ? transcriptions[transcriptions.length - 1].transcription_text : "";
  // Auto-scroll to bottom when new transcriptions arrive
  useEffect(() => {
    const el = scrollAreaRef.current;
    if (el && autoRefresh && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [transcriptions.length, lastTranscriptionText, autoRefresh]);

  const fetchIncompleteCalls = async () => {
    try {
      const response = await axios.get(`${API}/callmaster/incomplete/list`);
      setIncompleteCalls(response.data);
    } catch (error) {
      console.error("Error fetching calls:", error);
      toast.error("Failed to load calls");
    }
  };

  const fetchTranscriptions = async (call, silent = false) => {
    if (!call) return;

    try {
      setIsLoading(!silent);
      const response = await axios.get(`${API}/transcriptions`, {
        params: {
          ticker: call.ticker,
          calldatetime: call.calldatetime
        }
      });
      setTranscriptions(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching transcriptions:", error);
      if (!silent) {
        toast.error("Failed to load transcriptions");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCallSelect = (callId) => {
    const call = incompleteCalls.find(c => c.id === callId);
    setSelectedCall(call);
    if (call) {
      fetchTranscriptions(call);
      setAutoRefresh(true);
    } else {
      setTranscriptions([]);
      setAutoRefresh(false);
    }
  };

  const copyAllText = async () => {
    const allText = getContinuousText();
    try {
      await navigator.clipboard.writeText(allText);
      toast.success("Text copied to clipboard");
    } catch (error) {
      console.error("Failed to copy text:", error);
      toast.error("Failed to copy text");
    }
  };

  // Get continuous text with minute markers
  const getContinuousText = () => {
    return transcriptions
      .filter(t => t.transcription_text && !t.transcription_text.includes("[FAILED]"))
      .map(t => t.transcription_text)
      .join(" ");
  };

  // Render text with highlighted minute markers
  const renderContinuousText = () => {
    const text = getContinuousText();
    if (!text) return null;

    // Split by minute markers but keep them
    const parts = text.split(/(<<\d{2}:\d{2}>>)/g);
    
    return parts.map((part, index) => {
      if (part.match(/<<\d{2}:\d{2}>>/)) {
        return (
          <span 
            key={index} 
            className="inline-block bg-volt-blue text-white px-2 py-0.5 rounded text-sm font-bold mx-1 font-jetbrains"
          >
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  return (
    <div className="min-h-screen bg-deepspace-bg p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-outfit text-[#EDEDED]">
              Realtime Console
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Live transcription
            </p>
          </div>
          {lastUpdate && (
            <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
              <Clock className="w-4 h-4" />
              {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* Call Selection */}
        <Card className="bg-deepspace-surface border-deepspace-border p-6">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <label className="block text-sm text-[#A1A1AA] mb-2">
                Select Call
              </label>
              <select
                value={selectedCall?.id || ""}
                onChange={(e) => handleCallSelect(e.target.value)}
                className="w-full bg-deepspace-bg border border-deepspace-border text-[#EDEDED] rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-volt-blue"
                data-testid="realtime-call-dropdown"
              >
                <option value="">-- Select a call --</option>
                {incompleteCalls.map((call) => (
                  <option key={call.id} value={call.id}>
                    {new Date(call.calldatetime).toLocaleString()} - {call.ticker}
                  </option>
                ))}
              </select>
            </div>
            
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              disabled={!selectedCall}
              className={`${autoRefresh ? 'bg-green-600 hover:bg-green-700' : 'bg-deepspace-surfaceHighlight hover:bg-deepspace-surfaceHighlight/80'} text-white`}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              {autoRefresh ? "Live" : "Start Live"}
            </Button>

            <Button
              onClick={() => fetchTranscriptions(selectedCall)}
              disabled={!selectedCall || isLoading}
              className="bg-volt-blue hover:bg-volt-blue/80 text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </Card>

        {/* Transcription Display */}
        {selectedCall && (
          <Card className="bg-deepspace-surface border-deepspace-border p-6">
            {/* Header Bar */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-deepspace-border">
              <div>
                <h2 className="text-xl font-semibold font-outfit text-[#EDEDED]">
                  {selectedCall.ticker}
                </h2>
                <p className="text-xs text-[#A1A1AA]">
                  {new Date(selectedCall.calldatetime).toLocaleString()}
                </p>
              </div>
              
              <Button
                onClick={copyAllText}
                disabled={transcriptions.length === 0}
                className="bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white"
                data-testid="copy-all-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
            </div>

            {/* Live Indicator */}
            {autoRefresh && (
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-green-500">Live - Updating every 3 seconds</span>
              </div>
            )}

            {transcriptions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎙️</div>
                <p className="text-[#A1A1AA] text-lg">
                  {isLoading ? "Loading..." : "Waiting for transcription..."}
                </p>
                {autoRefresh && (
                  <p className="text-xs text-green-500 mt-2">
                    Live mode active - text will appear here
                  </p>
                )}
              </div>
            ) : (
              /* Continuous Text View */
              <div className="relative">
              <JumpToLatest visible={showJump} onClick={jumpToLatest} testId="realtime-jump-to-latest-btn" />
              <div
                ref={scrollAreaRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                  stickToBottomRef.current = atBottom;
                  setShowJump(!atBottom);
                }}
                className="bg-deepspace-bg rounded-lg p-6 h-[60vh] transcript-container"
                data-testid="realtime-transcript-scroll-area"
              >
                <p className="text-[#EDEDED] text-lg leading-relaxed whitespace-pre-wrap">
                  {renderContinuousText()}
                  <span ref={transcriptEndRef} />
                </p>
              </div>
              </div>
            )}
          </Card>
        )}

        {/* Help Text */}
        {!selectedCall && (
          <div className="text-center text-[#A1A1AA] py-8">
            <p>Select a call above to view real-time transcriptions</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealtimeConsole;
